import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useDicomStore } from "../../../redux/dicomRedux";
import { anatomyConstants } from "../../../constants/anatomyConstants";

const SectionViewModal = ({
  sectionViewMode,
  setSectionViewMode,
  applySectionView,
  planeOffset,
  setPlaneOffset,
  showHelper,
  setShowHelper,
  setInvertClip,
  closeSectionViewModal,
  isMobile,
}) => {
  const volume = useDicomStore((s) => s.volume);
  const axialCount = volume?.depth || 1;
  const coronalCount = volume?.rows || 1;
  const sagittalCount = volume?.cols || 1;

  const midForMode = (mode) => {
    const max =
      mode === "axial"
        ? Math.max(0, axialCount - 1)
        : mode === "coronal"
        ? Math.max(0, coronalCount - 1)
        : mode === "sagittal"
        ? Math.max(0, sagittalCount - 1)
        : 0;
    return Math.floor(max / 2);
  };

  const handleClick = (mode) => {
    setSectionViewMode(mode);
    const mid = midForMode(mode);
    setPlaneOffset(mid);
    requestAnimationFrame(() => applySectionView());
  };

  const handleHelperToggle = (e) => {
    e.stopPropagation();
    setShowHelper((prev) => !prev);
    applySectionView();
  };

  const handleInvertToggle = (e) => {
    e.stopPropagation();
    setInvertClip((prev) => {
      const next = !prev;
      applySectionView({ invertClip: next });
      return next;
    });
  };

  let sliderRange = { min: 0, max: 0, step: 1 };
  let currentMax = 0;
  switch (sectionViewMode) {
    case "axial":
      currentMax = axialCount - 1;
      break;
    case "coronal":
      currentMax = coronalCount - 1;
      break;
    case "sagittal":
      currentMax = sagittalCount - 1;
      break;
    default:
      currentMax = 0;
  }
  if (sectionViewMode !== "none") {
    sliderRange = { min: 0, max: currentMax, step: 1 };
  }

  const handleSliderChange = (e) => {
    setPlaneOffset(Number(e.target.value));
    applySectionView();
  };

  const getButtonClasses = (mode) => {
    const base =
      "w-full py-2 px-3 text-sm font-inter font-medium text-[#757575] rounded border transition focus:outline-none";
    const selected = sectionViewMode === mode;

    return selected
      ? `${base} bg-gray-100 border-2 border-[var(--main-color)]`
      : `${base} bg-white border border-[#0000001A] hover:bg-gray-50`;
  };

  // Check if mobile portrait (upside down) view
  const isMobilePortrait =
    isMobile &&
    typeof window !== "undefined" &&
    window.innerWidth < window.innerHeight;

  return (
    <div
      className={`absolute ${
        isMobilePortrait ? "bottom-20 left-2" : "bottom-1 left-28"
      } w-auto flex flex-col items-center z-50`}
    >
      <div
        className="flex flex-col items-start space-y-3 p-3 pt-6 bg-white rounded-lg shadow-lg border border-gray-200 w-40"
        style={{
          "--main-color": anatomyConstants.mainColor,
          "--main-color-bold": anatomyConstants.mainColorBold,
        }}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            closeSectionViewModal();
          }}
          aria-label="Close"
          className="absolute top-2 right-2 w-5 h-5 rounded-full bg-gray-100 border border-[#0000001A] shadow-sm flex items-center justify-center text-gray-700 hover:bg-gray-200 z-10"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClick("axial");
          }}
          className={getButtonClasses("axial")}
        >
          Axial
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClick("coronal");
          }}
          className={getButtonClasses("coronal")}
        >
          Sagittal
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClick("sagittal");
          }}
          className={getButtonClasses("sagittal")}
        >
          Coronal
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClick("none");
          }}
          className={getButtonClasses("none")}
        >
          None
        </button>
        {sectionViewMode !== "none" && (
          <div className="w-full mt-2">
            <input
              type="range"
              min={sliderRange.min}
              max={sliderRange.max}
              step={sliderRange.step}
              value={planeOffset}
              onChange={handleSliderChange}
              className="w-full accent-[var(--main-color)]"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-[var(--main-color)]">
                Slice: {planeOffset} / {sliderRange.max}
              </span>
              <SwapHorizIcon
                onClick={handleInvertToggle}
                className="cursor-pointer text-[var(--main-color)] hover:text-[var(--main-color-bold)]"
                fontSize="small"
                titleAccess="Show other half"
              />
            </div>
          </div>
        )}
        <button
          title="Toggle Cutting Plane"
          onClick={handleHelperToggle}
          className="w-full flex items-center space-x-2 py-2 px-3 text-sm font-medium text-white bg-[var(--main-color)] rounded hover:bg-[var(--main-color-bold)]"
        >
          {showHelper ? (
            <>
              <VisibilityIcon fontSize="small" />
              <span>Hide Helper</span>
            </>
          ) : (
            <>
              <VisibilityOffIcon fontSize="small" />
              <span>Show Helper</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SectionViewModal;
